import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { PrakurtiCirculerLogo } from '../../../../components/imagepath';

// import { PrakurtiCirculerLogo } from '../../../components/imagepath';

/**
 * Custom hook for detox retreat booking table columns configuration
 * @returns {Array} Column configuration for AdvancedTable
 */
export const useDetoxRetreatColumns = () => {
    const location = useLocation();
    const params = queryString.parse(location.search);
    const pageSize = 10;

    // Status color mapping (if status is available in future)
    const getStatusColor = (status) => {
        const colors = {
            'Upcoming': 'blue',
            'InProgress': 'orange',
            'Completed': 'green',
            'Cancelled': 'red',
        };
        return colors[status] || 'default';
    };

    return useMemo(() => [
        {
            id: 'sr',
            name: '#',
            selector: 'sr',
            width: 50,
            cell: (row, index) => {
                const currentPage = parseInt(params?.page) || 1;
                const calculatedIndex = (currentPage - 1) * pageSize + index + 1;
                return <span style={{ fontWeight: '500', color: '#666' }}>{calculatedIndex}</span>;
            },
        },
        {
            id: 'orderUniqueId',
            name: 'Order ID',
            selector: 'orderUniqueId',
            width: 160,
            cell: (record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {record?.isSimulation && (
                        <Tooltip title="Simulation Mode">
                            <img
                                src={PrakurtiCirculerLogo}
                                alt="sim"
                                style={{ width: '16px', height: '16px', borderRadius: '50%' }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={`Order: ${record?.orderUniqueId} | Click to view details`}>
                        <Link
                            to={`/order-history-overview/${record?.orderId}`}
                            style={{ fontWeight: '500', color: '#1890ff' }}
                        >
                            {record?.orderUniqueId || '-'}
                        </Link>
                    </Tooltip>
                </div>
            ),
        },
        {
            id: 'customerInfo',
            name: 'Customer',
            selector: 'customerName',
            width: 200,
            cell: (record) => (
                <Tooltip
                    title={
                        <div>
                            <div><strong>Name:</strong> {record?.customerName || '-'}</div>
                            <div><strong>Phone:</strong> {record?.phoneNo || '-'}</div>
                            {record?.parentCustomerName && (
                                <div><strong>Primary:</strong> {record?.parentCustomerName}</div>
                            )}
                        </div>
                    }
                >
                    <div style={{ cursor: 'pointer' }}>
                        <div style={{ fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record?.customerName || '-'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{record?.phoneNo || '-'}</div>
                        {record?.parentCustomerName && (
                            <div style={{ fontSize: '11px', color: '#1890ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Primary: {record?.parentCustomerName}
                            </div>
                        )}
                    </div>
                </Tooltip>
            ),
        },
        {
            id: 'retreatData',
            name: 'Retreat',
            selector: row => row.detoxRetreatData?.[0]?.detoxRetreatName || '-',
            cell: record => (
                <Tooltip title={record?.detoxRetreatData?.[0]?.detoxRetreatName || '-'}>
                    <Tag color="purple" style={{ marginBottom: '2px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {record?.detoxRetreatData?.[0]?.detoxRetreatName || '-'}
                    </Tag>
                </Tooltip>
            ),
            sortable: true,
            width: 180,
        },
        {
            id: 'schedule',
            name: 'Schedule',
            selector: 'startDate',
            width: 160,
            cell: (record) => {
                const startDate = record.startDate ? dayjs(record.startDate).format('DD MMM YYYY') : '-';
                const endDate = record.endDate ? dayjs(record.endDate).format('DD MMM YYYY') : '-';
                const startShort = record.startDate ? dayjs(record.startDate).format('DD MMM') : '-';
                const endShort = record.endDate ? dayjs(record.endDate).format('DD MMM YY') : '-';
                return (
                    <Tooltip title={`From: ${startDate} | To: ${endDate} | Duration: ${record?.totalDays || 0} days`}>
                        <div style={{ cursor: 'pointer' }}>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>{startShort} - {endShort}</div>
                            <div style={{ marginTop: '2px' }}>
                                <Tag color="cyan" style={{ fontSize: '11px' }}>
                                    {record?.totalDays || 0} {record?.totalDays === 1 ? 'Day' : 'Days'}
                                </Tag>
                                {record?.isReschedule && (
                                    <Tag color="orange" style={{ fontSize: '10px' }}>Rescheduled</Tag>
                                )}
                            </div>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            id: 'careTaker',
            name: 'Care Taker',
            selector: 'careTakerCount',
            width: 100,
            cell: (record) => (
                <Tooltip title={record?.careTakerCount > 0 ? `Care Takers: ${record?.careTakerCount}${record?.careTakerName ? ` (${record?.careTakerName})` : ''}` : 'No care taker'}>
                    <div style={{ cursor: 'pointer' }}>
                        {record?.careTakerCount > 0 ? (
                            <div>
                                <Tag color="geekblue">{record?.careTakerCount}</Tag>
                                {record?.careTakerName && (
                                    <div style={{ fontSize: '11px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }}>
                                        {record?.careTakerName}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span style={{ color: '#999', fontSize: '12px' }}>None</span>
                        )}
                    </div>
                </Tooltip>
            ),
        },
        {
            id: 'bookedOn',
            name: 'Booked On',
            selector: 'createdAt',
            width: 110,
            cell: (record) => {
                const fullDate = record?.createdAt ? dayjs(record.createdAt).format('DD MMM YYYY, hh:mm A') : '-';
                return (
                    <Tooltip title={`Booked on: ${fullDate}${record?.createdByName ? ` | By: ${record?.createdByName}` : ''}`}>
                        <div style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }}>
                            {record?.createdAt ? dayjs(record.createdAt).format('DD MMM YY') : '-'}
                            {record?.createdByName && (
                                <div style={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90px' }}>
                                    by {record?.createdByName}
                                </div>
                            )}
                        </div>
                    </Tooltip>
                );
            },
        },
    ], [params?.page]);
};